import {getMedicationPrimaryDoseLabel,getPlanHistoryDoseLabel} from '../../../features/medications/medicationDosePresentation';
import React from "react";
import {
  Box,
  Button,
  IconButton,
  Chip,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import colors from "../../../assets/theme/colors";
import { USER_ROLES } from "../../../constants/roles";
import {
  CareSourceStatusPanel,
  CARE_SOURCE_CONTEXTS,
} from "../../../features/careSourceProcessing";
import MedicationSetupRestrictedDialog from "./MedicationSetupRestrictedDialog";
import MedicationQuickLogExampleCard from "./MedicationQuickLogExampleCard";
import MedicationSourceCaptureCard from "./MedicationSourceCaptureCard";
import MedicationManagerStepSection from './MedicationManagerStepSection';
import { resolveMedicationProviderDisplay } from "./childMedicationHelpers";
import MedicationRemindersPanel from '../../../features/medicationReminders/MedicationRemindersPanel';
import {
  subscribeToMedicationReminderRounds,
  subscribeToMedicationReminderSchedules,
} from '../../../services/medicationReminderService';

const formatTimeLabel = (timeValue = "") => {
  const raw = String(timeValue || "").trim();
  if (!raw) {
    return "";
  }

  const [hours, minutes] = raw.split(":").map((value) => Number(value));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return raw;
  }

  const preview = new Date();
  preview.setHours(hours, minutes, 0, 0);
  return preview.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const normalizePlanDisplayText = (value = "") => (
  String(value || "").trim().replace(/\s+/g, " ")
);

const getMeaningfulOptionalText = (value = "") => {
  const text = normalizePlanDisplayText(value);
  if (!text || /^[—-]+$/.test(text) || /^(?:injection|pill|tablet|capsule|liquid)\s*[—-]$/i.test(text)) {
    return "";
  }
  return text;
};

const truncatePlanDisplayText = (value = "", maxLength = 180) => {
  const text = normalizePlanDisplayText(value);
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 3).trim()}...`;
};

const getMedicationNotesPreview = (entry = {}) => (
  truncatePlanDisplayText(getMeaningfulOptionalText(entry?.notes))
);

const getMedicationFormLabel = (entry = {}) => {
  const form = String(entry?.form || "").trim();
  const labels = {
    pill: "Pill",
    tablet: "Tablet",
    capsule: "Capsule",
    liquid: "Liquid",
    drops: "Drops",
    cream: "Cream",
    suppository: "Suppository",
    inhaler: "Inhaler",
  };
  return labels[form] || "";
};

const getMedicationDoseAndFormLabel = (entry = {}) => (
  [getMedicationPrimaryDoseLabel(entry), getMedicationFormLabel(entry)].filter(Boolean).join(" · ")
);

const weekdayLabels = {
  sun: "Su",
  mon: "Mo",
  tue: "Tu",
  wed: "We",
  thu: "Th",
  fri: "Fr",
  sat: "Sa",
};

const getMedicationScheduleTimesLabel = (entry = {}) => {
  const schedules = Array.isArray(entry?.schedules) ? entry.schedules : [];
  return schedules
    .map((schedule) => formatTimeLabel(schedule?.time || ""))
    .filter(Boolean)
    .join(", ");
};

const getShortCourseProgressLabel = (entry = {}) => {
  const courseDays = Number(entry?.courseDays || 0);
  const startDate = String(entry?.courseStartDate || entry?.startDate || "").trim();
  if (!courseDays || !startDate) {
    return "";
  }

  const start = new Date(`${startDate}T12:00:00`);
  if (Number.isNaN(start.getTime())) {
    return "";
  }

  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const elapsed = Math.floor((today.getTime() - start.getTime()) / 86400000) + 1;
  const currentDay = Math.max(1, Math.min(courseDays, elapsed));
  return `Day ${currentDay} of ${courseDays}`;
};

const getTodayDateKey = () => new Date().toISOString().slice(0, 10);

const getTodayWeekdayKey = () => (
  ["sun", "mon", "tue", "wed", "thu", "fri", "sat"][new Date().getDay()]
);

const addDaysToDateKey = (dateKey = "", daysToAdd = 0) => {
  if (!dateKey) return "";
  const date = new Date(`${dateKey}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().slice(0, 10);
};

const getShortCourseEndDateKey = (entry = {}) => {
  const explicitEndDate = String(entry?.courseEndDate || "").trim();
  if (explicitEndDate) return explicitEndDate;

  const courseDays = Number(entry?.courseDays || 0);
  const startDate = String(entry?.courseStartDate || entry?.startDate || "").trim();
  if (!courseDays || !startDate) return "";

  return addDaysToDateKey(startDate, Math.max(0, courseDays - 1));
};

export const getMedicationCareCues = (entry = {}) => {
  const cues = [];
  const scheduleType = String(entry?.scheduleType || "scheduled").trim();

  if (scheduleType === "short_course") {
    const endDate = getShortCourseEndDateKey(entry);
    const today = getTodayDateKey();
    if (endDate && today > endDate) {
      cues.push({ label: "Course may be complete", tone: "warning" });
    } else {
      const progressLabel = getShortCourseProgressLabel(entry);
      if (progressLabel) {
        cues.push({ label: progressLabel, tone: "info" });
      }
    }
  }

  if (scheduleType === "specific_days") {
    const selectedDays = Array.isArray(entry?.selectedDays) ? entry.selectedDays : [];
    if (selectedDays.length && !selectedDays.includes(getTodayWeekdayKey())) {
      cues.push({ label: "Not scheduled today", tone: "muted" });
    }
  }

  if (scheduleType === "every_x_hours" && !String(entry?.intervalHours || "").trim()) {
    cues.push({ label: "Needs interval", tone: "warning" });
  }

  return cues;
};

const getMedicationScheduleDisplay = (entry = {}) => {
  const scheduleType = String(entry?.scheduleType || "scheduled").trim();
  const times = getMedicationScheduleTimesLabel(entry);

  if (scheduleType === "as_needed") {
    return {
      label: "As needed",
      detail: [entry.asNeededReason, entry.maxFrequency].filter(Boolean).join(" · "),
      tone: "warm",
      icon: NotificationsNoneRoundedIcon,
    };
  }

  if (scheduleType === "occasionally") {
    return {
      label: "Occasionally",
      detail: entry.purpose || entry.notes || "",
      tone: "neutral",
      icon: CalendarTodayRoundedIcon,
    };
  }

  if (scheduleType === "short_course") {
    return {
      label: "Short course",
      detail: [getShortCourseProgressLabel(entry), times].filter(Boolean).join(" · "),
      tone: "coral",
      icon: CalendarTodayRoundedIcon,
    };
  }

  if (scheduleType === "specific_days") {
    const days = Array.isArray(entry?.selectedDays)
      ? entry.selectedDays.map((day) => weekdayLabels[day] || day).filter(Boolean).join(" · ")
      : "";
    return {
      label: "Specific days",
      detail: [days, times].filter(Boolean).join(" · "),
      tone: "purple",
      icon: CalendarTodayRoundedIcon,
    };
  }

  if (scheduleType === "every_x_hours") {
    return {
      label: "Every X hours",
      detail: [
        entry.intervalHours ? `Every ${entry.intervalHours} hours` : "",
        entry.intervalStartTime ? `from ${formatTimeLabel(entry.intervalStartTime)}` : "",
      ].filter(Boolean).join(" · "),
      tone: "blue",
      icon: AccessTimeRoundedIcon,
    };
  }

  return {
    label: "Scheduled",
    detail: times,
    tone: "green",
    icon: AccessTimeRoundedIcon,
  };
};

const schedulePillThemes = {
  green: { bg: "#DCEFE7", color: colors.semantic.sageDeep },
  purple: { bg: "#E8DDF4", color: "#5B4677" },
  warm: { bg: "#FCEBD4", color: "#9B5B19" },
  coral: { bg: "#F5DDD2", color: "#85432D" },
  blue: { bg: "#DEEAF6", color: "#42698C" },
  neutral: { bg: colors.semantic.profileFlow.notStartedBg, color: colors.semantic.ink2 },
};

const getMedicationScheduleSentence = (entry = {}, scheduleDisplay = {}) => {
  const sourceSchedule = normalizePlanDisplayText(entry.sourceScheduleText || entry.schedule || "");
  if (sourceSchedule) return sourceSchedule.replace(/^—\s*/, "");
  if (scheduleDisplay.label === "Scheduled") return scheduleDisplay.detail || "Schedule not set";
  return [scheduleDisplay.label, scheduleDisplay.detail].filter(Boolean).join(" · ");
};

const cuePillThemes = {
  info: { bg: "#E7F4EF", color: colors.semantic.sageDeep },
  muted: { bg: colors.semantic.profileFlow.notStartedBg, color: colors.semantic.ink2 },
  warning: { bg: alpha(colors.semantic.warning, 0.15), color: colors.semantic.profileFlow.inProgressText },
};

const getMedicationGroupKey = (entry = {}, archived = false) => {
  if (entry?.medicationPlanAction === "complete_course") return "history_only";
  if (archived || entry?.isArchived) return "paused";
  const scheduleType = String(entry?.scheduleType || "scheduled").trim();
  if (["as_needed", "occasionally"].includes(scheduleType)) return "as_needed";
  if (scheduleType === "short_course") return "short_course";
  return "daily";
};

const medicationGroupDefinitions = [
  { key: "daily", label: "Daily & Regular" },
  { key: "as_needed", label: "As Needed & Occasional" },
  { key: "short_course", label: "Short Course" },
  { key: "paused", label: "Paused / Stopped" },
];

const getMedicationReviewLabel = (entry = {}) => {
  if (!entry?.needsReview) {
    return "";
  }

  const reviewReasons = Array.isArray(entry?.reviewReasons) ? entry.reviewReasons : [];
  if(entry.doseNeedsClarification || reviewReasons.includes("dose_unit_needed"))return "Dose and unit need clarification";
  if (reviewReasons.includes("missing_dose")) {
    return "Dose missing · Review";
  }
  if (reviewReasons.some((reason) => ["missing_schedule", "missing_timing", "unclear_schedule"].includes(reason))) {
    return "Schedule unclear · Review";
  }
  return "";
};


const isStoppedPlanHistoryEntry = (entry = {}) => (
  ["stopped", "paused", "archived"].includes(String(entry.type || "").toLowerCase())
);

const isDoseChangePlanHistoryEntry = (entry = {}) => (
  ["dose_change", "plan_change"].includes(String(entry.type || "").toLowerCase())
  || Boolean(entry.previousDose || entry.newDose)
);

const getPlanHistoryTone = (entry = {}) => {
  if (entry.type === "course_completed") {
    return {
      label: "Course completed",
      dot: colors.semantic.neutral,
      bg: colors.semantic.profileFlow.notStartedBg,
      ink: colors.semantic.ink2,
    };
  }
  if (entry.type === "current_plan") {
    return {
      label: entry.isArchived ? "Paused" : "Current plan",
      dot: entry.isArchived ? colors.semantic.neutral : colors.semantic.sageDeep,
      bg: entry.isArchived ? colors.semantic.profileFlow.notStartedBg : colors.semantic.profileFlow.completionBg,
      ink: entry.isArchived ? colors.semantic.ink2 : colors.semantic.profileFlow.completionText,
    };
  }

  if (entry.type === "previous_plan") {
    return {
      label: "Previous plan",
      dot: colors.semantic.ink2,
      bg: colors.semantic.profileFlow.notStartedBg,
      ink: colors.semantic.ink2,
    };
  }

  if (isStoppedPlanHistoryEntry(entry)) {
    return {
      label: "Stopped",
      dot: colors.semantic.neutral,
      bg: colors.semantic.profileFlow.notStartedBg,
      ink: colors.semantic.ink2,
    };
  }

  if (isDoseChangePlanHistoryEntry(entry)) {
    return {
      label: "Dose increased",
      dot: colors.semantic.warning,
      bg: alpha(colors.semantic.warning, 0.14),
      ink: colors.semantic.profileFlow.inProgressText,
    };
  }

  return {
    label: "Started",
    dot: colors.semantic.sageDeep,
    bg: colors.semantic.profileFlow.completionBg,
    ink: colors.semantic.profileFlow.completionText,
  };
};

const getPlanHistoryDateLabel = (entry = {}) => {
  const exactDate = normalizePlanDisplayText(entry.date || entry.effectiveDate || entry.savedAt || entry.effectiveFrom || "");
  const approximateDate = normalizePlanDisplayText(entry.approximateDateText || "");
  const dateLabel = approximateDate || exactDate || "Date needs review";
  if (entry.dateApproximate && approximateDate) {
    return `${entry.type === "started" ? "Start" : "Change"} date is approximate: ${dateLabel}`;
  }
  return dateLabel;
};

const getPlanHistorySummary = (entry = {}) => {
  if (entry.summary) {
    return entry.summary;
  }

  const schedule = String(entry.schedule || "").trim();
  if (entry.type === "started") {
    return [
      "Started at",
      getPlanHistoryDoseLabel(entry.dose, entry.unit),
      schedule,
    ].filter(Boolean).join(" ");
  }

  const previousDose = getPlanHistoryDoseLabel(entry.previousDose, entry.previousUnit);
  const newDose = getPlanHistoryDoseLabel(entry.newDose, entry.newUnit);
  if (previousDose || newDose) {
    const doseChange = previousDose && newDose
      ? `from ${previousDose} -> ${newDose}`
      : [previousDose ? `from ${previousDose}` : "", newDose ? `to ${newDose}` : ""].filter(Boolean).join(" ");

    return [
      "Dose increased",
      doseChange,
      schedule,
    ].filter(Boolean).join(" ");
  }

  return entry.summary || "Medication plan changed";
};

const planHistoryNeedsReview = (entry = {}) => (
  Boolean(entry.needsReview || entry.dateApproximate)
);

const getPlanHistoryReviewLabel = (entry = {}) => (
  entry.dateApproximate || entry.approximateDateText ? "Approximate date" : "Date missing"
);

const getMedicationPlanHistoryEntries = (medications = []) => (
  medications.flatMap((medication) => {
    const medicationName = medication.name || "Medication";
    const importedEntries = Array.isArray(medication?.planHistory)
      ? medication.planHistory.map((entry) => ({
          ...entry,
          medicationName,
          sortDate: entry.date || entry.effectiveDate || entry.approximateDateText || medication.savedAt || "",
        }))
      : [];
    const previousEntries = Array.isArray(medication?.history)
      ? medication.history.map((entry, index) => {
          const scheduleDisplay = getMedicationScheduleDisplay(entry);
          const doseAndForm = getMedicationDoseAndFormLabel(entry);
          return {
            id: entry.id ? `${entry.id}-previous-${index}` : `${medication.id || medicationName}-previous-${index}`,
            type: "previous_plan",
            medicationName,
            savedAt: entry.savedAt || entry.createdAt || entry.effectiveFrom || "",
            sortDate: entry.savedAt || entry.createdAt || entry.effectiveFrom || "",
            summary: [
              doseAndForm,
              [scheduleDisplay.label, scheduleDisplay.detail].filter(Boolean).join(" · "),
            ].filter(Boolean).join(" · ") || "Previous plan saved",
          };
        })
      : [];
    const currentScheduleDisplay = getMedicationScheduleDisplay(medication);
    const currentDoseAndForm = getMedicationDoseAndFormLabel(medication);
    const currentEntry = medicationName && medication.medicationPlanAction !== "complete_course"
      ? [{
          id: `${medication.id || medicationName}-current`,
          type: "current_plan",
          medicationName,
          isArchived: Boolean(medication.isArchived),
          savedAt: medication.savedAt || medication.createdAt || medication.effectiveFrom || medication.startDate || "",
          sortDate: medication.savedAt || medication.createdAt || medication.effectiveFrom || medication.startDate || "",
          summary: [
            currentDoseAndForm,
            [currentScheduleDisplay.label, currentScheduleDisplay.detail].filter(Boolean).join(" · "),
          ].filter(Boolean).join(" · ") || "Current medication plan",
        }]
      : [];

    return [...importedEntries, ...previousEntries, ...currentEntry];
  }).sort((left, right) => (
    String(right.sortDate || "").localeCompare(String(left.sortDate || ""))
  ))
);

const actionButtonSx = {
  width: 32,
  height: 32,
  borderRadius: 1.6,
  border: "1px solid transparent",
  color: colors.semantic.ink2,
  bgcolor: colors.semantic.profileFlow.notStartedBg,
  transition: "background-color 140ms ease, border-color 140ms ease, transform 140ms ease",
  "&:hover": {
    bgcolor: colors.landing.sageLight,
    borderColor: colors.semantic.profileFlow.cardBorder,
    transform: "translateY(-1px)",
  },
};

const lockedActionButtonSx = {
  ...actionButtonSx,
  color: colors.semantic.ink2,
  bgcolor: colors.semantic.profileFlow.notStartedBg,
  borderColor: colors.semantic.profileFlow.cardBorder,
  "&:hover": {
    bgcolor: colors.semantic.profileFlow.notStartedBg,
    borderColor: colors.semantic.profileFlow.progressPartial,
    transform: "none",
  },
};

const getMedicationSetupHelperText = (userRole) => (
  userRole === USER_ROLES.THERAPIST
    ? "You can still view shared medication details and add professional observations."
    : "You can still view medications and log medication doses."
);

const ChildMedicationManager = ({
  childId = "",
  childName = "",
  medications = [],
  providers = [],
  onEditMedication,
  onAddMedication,
  onArchiveMedication,
  onMedicationListChanged,
  onMedicationReviewComplete,
  onOpenMedicationReview,
  onOpenMedicationReminders,
  medicationReviewNavigation = null,
  canEditMedication = true,
  userRole,
  onContactCareOwner,
}) => {
  const [restrictedAction, setRestrictedAction] = React.useState(null);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [reminderSchedules, setReminderSchedules] = React.useState([]);
  const [reminderRounds, setReminderRounds] = React.useState([]);
  const [pendingMedicationSourceReviewCount, setPendingMedicationSourceReviewCount] = React.useState(null);
  React.useEffect(() => {
    if (!childId) return undefined;
    const stopSchedules = subscribeToMedicationReminderSchedules(childId, setReminderSchedules, () => {});
    const stopRounds = subscribeToMedicationReminderRounds(childId, setReminderRounds, () => {});
    return () => { stopSchedules(); stopRounds(); };
  }, [childId]);
  const scheduledMedicationIds = React.useMemo(() => new Set([
    ...reminderSchedules.filter((schedule) => schedule.active && !schedule.paused).map((schedule) => schedule.medicationId),
    ...reminderRounds.filter((round) => round.active !== false).flatMap((round) => (round.members || []).map((member) => member.medicationId)),
  ]), [reminderRounds, reminderSchedules]);
  const activeMedications = medications.filter((entry) => !entry?.isArchived);
  const medicationGroups = medicationGroupDefinitions.map((group) => ({
    ...group,
    medications: medications.filter((entry) => getMedicationGroupKey(entry) === group.key),
  }));
  const planHistoryEntries = getMedicationPlanHistoryEntries(medications);
  const hasAnyMedications = medicationGroups.some((group) => group.medications.length > 0);
  const canLoadMedicationSources = Boolean(childId && canEditMedication);
  const showMedicationSources = canLoadMedicationSources && pendingMedicationSourceReviewCount !== 0;

  const openMedicationSetupRestriction = (action) => {
    setRestrictedAction(action || "medication setup");
  };

  const closeMedicationSetupRestriction = () => {
    setRestrictedAction(null);
  };

  const renderMedicationRow = (entry, index, archived = false) => {
    const doseAndFormLabel = getMedicationDoseAndFormLabel(entry);
    const scheduleDisplay = getMedicationScheduleDisplay(entry);
    const scheduleTheme = schedulePillThemes[scheduleDisplay.tone] || schedulePillThemes.neutral;
    const purpose = getMeaningfulOptionalText(entry?.purpose);
    const notesPreview = getMedicationNotesPreview(entry);
    const providerDisplay = resolveMedicationProviderDisplay(entry, providers);
    const isPaused = Boolean(entry?.isArchived);
    const canModifyEntry = canEditMedication;
    const hasActiveReminder = scheduledMedicationIds.has(entry.id);
    const reviewLabel = getMedicationReviewLabel(entry);
    const careCues = archived ? [] : getMedicationCareCues(entry);
    const scheduleSentence = getMedicationScheduleSentence(entry, scheduleDisplay);
    const iconBg = archived
      ? "#DDEFE6"
      : scheduleDisplay.tone === "warm"
        ? "#FCEBD4"
        : scheduleDisplay.tone === "coral"
          ? "#F5DDD2"
          : scheduleDisplay.tone === "purple"
            ? "#E8DDF4"
            : "#DCEFE7";
    const iconColor = archived
      ? colors.semantic.ink2
      : scheduleTheme.color;

    return (
      <Box
        key={entry.id}
        aria-label={`${entry.name || `Medication ${index + 1}`} medication`}
        sx={{
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr) auto",
          alignItems: "start",
          gap: { xs: 1.05, sm: 1.2 },
          width: "100%",
          py: { xs: 1.3, sm: 1.45 },
          px: 0,
          borderRadius: 0,
          borderBottom: `1px solid ${alpha(colors.semantic.profileFlow.cardBorder, 0.72)}`,
          bgcolor: "transparent",
          boxShadow: "none",
          opacity: archived ? 0.62 : 1,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: iconBg,
            color: iconColor,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <MedicationRoundedIcon sx={{ fontSize: 18 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 850,
              color: colors.landing.heroText,
              lineHeight: 1.08,
              fontSize: { xs: "1rem", sm: "1.1rem" },
            }}
            noWrap
          >
            {entry.name || (archived ? "Archived medication" : `Medication ${index + 1}`)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 0.15,
              lineHeight: 1.3,
              fontSize: "0.88rem",
              color: colors.semantic.ink2,
            }}
          >
            {doseAndFormLabel || (archived ? "Paused medication" : "Saved medication")}
          </Typography>

          <Typography sx={{ mt: 0.55, color: colors.semantic.ink, fontWeight: 750, fontSize: "0.82rem", lineHeight: 1.35 }}>
            {scheduleSentence}
          </Typography>

          {providerDisplay.label ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.15,
                lineHeight: 1.3,
                fontSize: "0.8rem",
                color: colors.semantic.ink2,
              }}
            >
              <Box component="span" sx={{ fontWeight: 900 }}>
                {providerDisplay.source === "provider" ? "Provider" : "Prescriber"}
              </Box>
              {" · "}
              {providerDisplay.label}
            </Typography>
          ) : null}

          {(purpose || notesPreview) ? (
            <Box
              sx={{
                mt: 0.75,
                pt: 0.75,
                borderTop: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
              }}
            >
              {purpose ? (
                <Typography variant="body2" sx={{ color: colors.semantic.ink2, fontSize: "0.8rem", lineHeight: 1.35 }}>
                  <Box component="span" sx={{ fontWeight: 900 }}>For</Box>
                  {" · "}
                  {purpose}
                </Typography>
              ) : null}
              {notesPreview ? (
                <Typography
                  variant="body2"
                  title={notesPreview}
                  sx={{
                    mt: purpose ? 0.25 : 0,
                    color: colors.semantic.ink2,
                    fontSize: "0.8rem",
                    lineHeight: 1.35,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 900 }}>Note</Box>
                  {" · "}
                  {notesPreview}
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {(isPaused || reviewLabel || careCues.length) ? (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.7, flexWrap: "wrap", rowGap: 0.5 }}>
            {isPaused ? <Chip
              label="Paused"
              size="small"
              sx={{ height: 22, borderRadius: "999px", fontSize: "0.72rem", fontWeight: 850, bgcolor: colors.semantic.profileFlow.notStartedBg, color: colors.semantic.ink2 }}
            /> : null}
            {reviewLabel ? (
              <Button
                onClick={() => onEditMedication?.(entry)}
                aria-label={reviewLabel}
                size="small"
                sx={{
                  minHeight: 26,
                  borderRadius: "999px",
                  fontSize: "0.72rem",
                  fontWeight: 850,
                  bgcolor: alpha(colors.semantic.warning, 0.14),
                  color: colors.semantic.profileFlow.inProgressText,
                  textTransform: "none",
                  px: 0.9,
                }}
              >
                {reviewLabel}
              </Button>
            ) : null}
            {careCues.map((cue) => {
              const cueTheme = cuePillThemes[cue.tone] || cuePillThemes.muted;
              return (
                <Chip
                  key={`${entry.id}-${cue.label}`}
                  label={cue.label}
                  size="small"
                  sx={{
                    height: 22,
                    borderRadius: "999px",
                    fontSize: "0.72rem",
                    fontWeight: 850,
                    bgcolor: cueTheme.bg,
                    color: cueTheme.color,
                    maxWidth: "fit-content",
                  }}
                />
              );
            })}
          </Stack>
          ) : null}
        </Box>

        <Stack
          direction="row"
          spacing={0.45}
          sx={{
            alignItems: "center",
            justifySelf: "end",
            ml: 0.5,
            mt: 0.05,
          }}
        >
          {!archived ? (
            <IconButton
              size="small"
              onClick={() => onOpenMedicationReminders?.(entry)}
              aria-label={`${canModifyEntry ? (hasActiveReminder ? "Manage" : "Set") : "View"} reminder for ${entry.name || `medication ${index + 1}`}`}
              sx={{
                ...actionButtonSx,
                bgcolor: hasActiveReminder ? colors.semantic.profileFlow.completionBg : colors.semantic.profileFlow.notStartedBg,
                color: hasActiveReminder ? colors.semantic.profileFlow.completionText : colors.semantic.ink2,
              }}
            >
              {hasActiveReminder ? <NotificationsActiveRoundedIcon fontSize="small" /> : <NotificationsNoneRoundedIcon fontSize="small" />}
            </IconButton>
          ) : null}
          {canModifyEntry ? (
            <>
              <IconButton
                size="small"
                onClick={() => onEditMedication?.(entry)}
                aria-label={`Edit medication ${entry.name || index + 1}`}
                sx={actionButtonSx}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onArchiveMedication?.(entry, !archived)}
                aria-label={`${archived ? "Resume" : "Pause"} medication ${entry.name || index + 1}`}
                sx={{
                  ...actionButtonSx,
                  bgcolor: archived ? colors.semantic.profileFlow.completionBg : colors.semantic.profileFlow.notStartedBg,
                  color: archived ? colors.semantic.profileFlow.completionText : colors.semantic.ink2,
                  "&:hover": {
                    bgcolor: archived ? colors.semantic.profileFlow.completionBg : colors.semantic.profileFlow.notStartedBg,
                    borderColor: colors.semantic.profileFlow.cardBorder,
                  },
                }}
              >
                {archived ? <PlayCircleOutlineRoundedIcon fontSize="small" /> : <PauseCircleOutlineRoundedIcon fontSize="small" />}
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={() => openMedicationSetupRestriction("edit medication")}
                aria-label={`Medication setup restricted: edit medication ${entry.name || index + 1}`}
                sx={lockedActionButtonSx}
              >
                <LockOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => openMedicationSetupRestriction(archived ? "resume medication" : "pause medication")}
                aria-label={`Medication setup restricted: ${archived ? "resume" : "pause"} medication ${entry.name || index + 1}`}
                sx={lockedActionButtonSx}
              >
                <LockOutlinedIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Stack
      spacing={2.35}
      sx={{
        width: "100%",
        bgcolor: colors.semantic.profileFlow.cardBg,
        px: { xs: 0.25, sm: 0.5 },
        py: { xs: 0.6, sm: 1 },
      }}
    >
      <Box sx={{ pb: 0.3 }}>
        <Typography sx={{ color: colors.semantic.ink2, fontSize: '0.94rem', lineHeight: 1.55 }}>
          This page is the one place your care circle checks for what {childName || "this child"} takes. Work top to bottom, then Save at the bottom.
        </Typography>
      </Box>

      <MedicationRemindersPanel
        childId={childId}
        childName={childName}
        medications={medications}
        canManage={canEditMedication}
        summaryOnly
        onOpen={onOpenMedicationReminders}
      />

      <MedicationManagerStepSection
        step={1}
        title="Current medications"
        description="The active list your care circle relies on right now."
        badge={`${activeMedications.length} active`}
        tone="current"
      >
        {hasAnyMedications ? (
          <Stack spacing={1.3}>
            {medicationGroups
              .filter((group) => group.medications.length > 0)
              .map((group) => (
                <Box key={group.key}>
                  <Typography
                    sx={{
                      mb: 0.7,
                      color: colors.semantic.ink2,
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {group.label} <Box component="span" sx={{ ml: 0.5 }}>{group.medications.length}</Box>
                  </Typography>
                  <Stack spacing={0}>
                    {group.medications.map((entry, index) => (
                      renderMedicationRow(entry, index, group.key === "paused")
                    ))}
                  </Stack>
                </Box>
              ))}
          </Stack>
        ) : (
          <Stack spacing={0.75}>
            <Box
              sx={{
                p: { xs: 1.4, sm: 1.75 },
                borderRadius: 2.5,
                border: `1px dashed ${colors.semantic.profileFlow.cardBorder}`,
                bgcolor: colors.semantic.profileFlow.cardBg,
              }}
            >
              <Typography sx={{ fontWeight: 850, color: colors.landing.heroText, lineHeight: 1.15 }}>
                No medications added yet
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.35, lineHeight: 1.3, color: colors.semantic.ink2 }}>
                {canEditMedication
                  ? "Add the current medication plan so everyone logs from the same list."
                  : "Medication setup is managed by the Care Owner or a Care Partner."}
              </Typography>
            </Box>
            {canEditMedication ? (
              <MedicationQuickLogExampleCard childName={childName} />
            ) : null}
          </Stack>
        )}
      </MedicationManagerStepSection>

      <MedicationManagerStepSection
        step={2}
        title="Add medication"
        description="Use the structured form for the cleanest medication list, or upload a document for review."
        tone="add"
      >
        {canEditMedication ? (
          <MedicationSourceCaptureCard
            childId={childId}
            onAddMedication={onAddMedication}
            showHeader={false}
          />
        ) : (
          <Stack spacing={0.8}>
            <Button
              onClick={() => openMedicationSetupRestriction("add medication")}
              variant="outlined"
              startIcon={<LockOutlinedIcon />}
              sx={{
                width: "100%",
                justifyContent: "center",
                textTransform: "none",
                fontWeight: 800,
                borderRadius: 2,
                color: colors.semantic.ink,
                borderStyle: "dashed",
                borderColor: colors.semantic.profileFlow.cardBorder,
                bgcolor: colors.semantic.profileFlow.cardBg,
                px: 1,
                py: 0.9,
                minHeight: 42,
              }}
            >
              {hasAnyMedications ? "Add medication" : "Add your first medication"}
            </Button>
            <Box
              sx={{
                p: 1.15,
                borderRadius: 2,
                border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
                bgcolor: colors.semantic.profileFlow.cardBg,
              }}
            >
              <Stack direction="row" spacing={0.8} alignItems="flex-start">
                <LockOutlinedIcon sx={{ fontSize: 18, color: colors.semantic.sageDeep, mt: 0.05 }} />
                <Box>
                  <Typography sx={{ fontWeight: 750, color: colors.landing.heroText, lineHeight: 1.25, fontSize: "0.84rem" }}>
                    Medication setup is managed by the Care Owner or a Care Partner.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, color: colors.semantic.ink2, lineHeight: 1.3 }}>
                    {getMedicationSetupHelperText(userRole)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        )}
      </MedicationManagerStepSection>

      {showMedicationSources ? <MedicationManagerStepSection
        step={3}
        title="Review uploaded source"
        description="We pulled these from a document you uploaded. Decide on each before it touches the list."
        badge={pendingMedicationSourceReviewCount > 0 ? `${pendingMedicationSourceReviewCount} to review` : ''}
        tone="review"
      >
        {canLoadMedicationSources ? (
          <CareSourceStatusPanel
            childId={childId}
            existingMedications={medications}
            sourceContext={CARE_SOURCE_CONTEXTS.MEDICATION}
            title="Medication source activity"
            onAddMedication={onAddMedication}
            onEditMedication={onEditMedication}
            onMedicationListChanged={onMedicationListChanged}
            onMedicationReviewComplete={onMedicationReviewComplete}
            onSetReminder={onOpenMedicationReminders}
            onOpenMedicationReview={onOpenMedicationReview}
            medicationReviewNavigation={medicationReviewNavigation}
            includeAllMatching
            onPendingReviewCountChange={setPendingMedicationSourceReviewCount}
          />
        ) : (
          <Box
            sx={{
              p: 1.3,
              borderRadius: 2,
              border: `1px dashed ${colors.semantic.profileFlow.cardBorder}`,
              bgcolor: colors.semantic.profileFlow.cardBg,
            }}
          >
            <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.84rem" }}>
              No uploaded medication source is waiting for review.
            </Typography>
          </Box>
        )}
      </MedicationManagerStepSection> : null}

      <MedicationManagerStepSection
        step={4}
        title="Medication history"
        description="Every dose change and where it came from. A timeline you can hand to a new clinician."
        badge={`${planHistoryEntries.length} ${planHistoryEntries.length === 1 ? "entry" : "entries"}`}
        tone="history"
        last
      >
        <Button
          type="button"
          variant="outlined"
          fullWidth
          aria-expanded={historyOpen}
          onClick={() => setHistoryOpen((current) => !current)}
          endIcon={(
            <ExpandMoreRoundedIcon
              sx={{
                transform: historyOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 160ms ease",
              }}
            />
          )}
          sx={{
            justifyContent: "space-between",
            minHeight: 44,
            borderRadius: 2,
            px: 1.2,
            color: colors.semantic.ink,
            borderColor: colors.semantic.profileFlow.cardBorder,
            bgcolor: colors.semantic.profileFlow.cardBg,
            textTransform: "none",
            fontWeight: 850,
            "&:hover": {
              bgcolor: colors.landing.sageLight,
              borderColor: colors.semantic.profileFlow.cardBorder,
            },
          }}
        >
          {historyOpen ? "Hide medication history" : "View medication history"}
        </Button>
        <Collapse in={historyOpen} unmountOnExit>
          <Box sx={{ mt: 0.85 }}>
            {planHistoryEntries.length > 0 ? (
          <Box
            sx={{
              p: { xs: 1.15, sm: 1.35 },
              borderRadius: 2.5,
              border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
              bgcolor: colors.semantic.profileFlow.cardBg,
              boxShadow: `0 8px 24px ${alpha(colors.landing.deepNavy, 0.05)}`,
            }}
          >
            <Stack>
              {planHistoryEntries.map((entry, index) => {
              const tone = getPlanHistoryTone(entry);
              const isLast = index === planHistoryEntries.length - 1;
              const sourceLabel = String(entry.sourceTitle || entry.sourceDocument?.title || "").trim();
              const reason = String(entry.reason || "").trim();
              const needsReview = planHistoryNeedsReview(entry);

              return (
                <Box
                  key={entry.id || `${entry.medicationName}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "18px minmax(0, 1fr)",
                    columnGap: 0.95,
                    alignItems: "start",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      minHeight: isLast ? 18 : "100%",
                      display: "flex",
                      justifyContent: "center",
                      pt: 0.3,
                      "&::after": isLast ? {} : {
                        content: '""',
                        position: "absolute",
                        top: 15,
                        bottom: -8,
                        left: "50%",
                        width: 2,
                        transform: "translateX(-50%)",
                        bgcolor: colors.semantic.profileFlow.cardBorder,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: tone.dot,
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 0, pb: isLast ? 0 : 1.15 }}>
                    <Stack direction="row" spacing={0.65} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 0.45 }}>
                      <Box
                        component="span"
                        sx={{
                          px: 0.65,
                          py: 0.18,
                          borderRadius: "999px",
                          bgcolor: tone.bg,
                          color: tone.ink,
                          fontSize: "0.68rem",
                          fontWeight: 850,
                          lineHeight: 1.2,
                        }}
                      >
                        {tone.label}
                      </Box>
                      <Typography sx={{ fontSize: "0.72rem", color: colors.semantic.ink2, lineHeight: 1.2 }}>
                        {getPlanHistoryDateLabel(entry)}
                      </Typography>
                      {needsReview ? (
                        <Box
                          component="span"
                          sx={{
                            px: 0.65,
                            py: 0.18,
                            borderRadius: "999px",
                            bgcolor: alpha(colors.semantic.warning, 0.14),
                            color: colors.semantic.profileFlow.inProgressText,
                            fontSize: "0.68rem",
                            fontWeight: 850,
                            lineHeight: 1.2,
                          }}
                        >
                          {getPlanHistoryReviewLabel(entry)}
                        </Box>
                      ) : null}
                    </Stack>
                    <Typography sx={{ mt: 0.35, fontSize: "0.82rem", fontWeight: 850, color: colors.landing.heroText }}>
                      {entry.medicationName}
                    </Typography>
                    <Typography sx={{ mt: 0.15, fontSize: "0.78rem", color: colors.semantic.ink, lineHeight: 1.35 }}>
                      {getPlanHistorySummary(entry)}
                    </Typography>
                    {reason ? (
                      <Typography sx={{ mt: 0.15, fontSize: "0.75rem", color: colors.semantic.ink2, lineHeight: 1.35 }}>
                        Reason: {reason}
                      </Typography>
                    ) : null}
                    {sourceLabel ? (
                      <Stack direction="row" spacing={0.35} alignItems="center" sx={{ mt: 0.35, minWidth: 0 }}>
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 13, color: colors.semantic.ink2, flexShrink: 0 }} />
                        <Typography
                          sx={{
                            minWidth: 0,
                            fontSize: "0.68rem",
                            color: colors.semantic.ink2,
                            lineHeight: 1.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sourceLabel}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Box>
                </Box>
              );
            })}
            </Stack>
          </Box>
        ) : (
          <Box
            sx={{
              p: 1.3,
              borderRadius: 2,
              border: `1px dashed ${colors.semantic.profileFlow.cardBorder}`,
              bgcolor: colors.semantic.profileFlow.cardBg,
            }}
          >
            <Typography sx={{ color: colors.semantic.ink2, fontSize: "0.84rem" }}>
              Medication changes will appear here after medications are added or updated.
            </Typography>
          </Box>
            )}
          </Box>
        </Collapse>
      </MedicationManagerStepSection>

      <MedicationSetupRestrictedDialog
        open={Boolean(restrictedAction)}
        onClose={closeMedicationSetupRestriction}
        userRole={userRole}
        onContactCareOwner={onContactCareOwner}
      />
    </Stack>
  );
};

export default ChildMedicationManager;
